<script setup>

import {inject, ref, nextTick, onMounted} from 'vue'
import API from "@/classes/global/API";

import DataGrid from "@/components/common/other/DataGrid";
import Button from "@/components/common/input/Button";
import CheckBox from "@/components/common/input/CheckBox";
import StaticWidget from "@/components/common/other/StaticWidget";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import DateInput from "@/components/common/input/DateInput";
import TextArea from "@/components/common/input/TextArea";
import DropDownTextArea from "@/components/common/input/DropDownTextArea";
import SearchPill from "@/components/common/other/SearchPill";
import SearchStockPillGroup from "@/components/warehouse/SearchStockPillGroup";
import ConsigneeDetail from "@/classes/bookings/ConsigneeDetail";
import GridCellDetail from "@/components/common/other/GridCellDetail";
import MoreInfoPopOutPanel from "@/components/common/other/MoreInfoPopOutPanel";
import StockDescriptions from "@/components/warehouse/StockDescriptions";

import {useStore} from 'vuex';
import {useRoute} from "vue-router";

import {useMapConsigneeAddress} from "@/composables/bookings/useMapConsigneeAddress";
import {useMapConsigneeContact} from "@/composables/bookings/useMapConsigneeContact";
import {useClearConsigneeContact} from "@/composables/bookings/useClearConsigneeContact";
import {useClearDescriptionHeaders} from "@/composables/stock/useClearDescriptionHeaders";
import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";

const store = useStore();
const route = useRoute();

const pickStock = inject("pickStock");
const uploadDocs = ref(true);

const props = defineProps({

  modelValue: {}
})

const emits = defineEmits(['update-picked-items']);


onMounted(() => {

  prepareMetaData();

})


async function prepareMetaData() {

  const response = await API.getStockMetaData(route, 'pickStockValidationFields' );

  if(response) pickStock.value.metaData = response;

}

async function getCompanyDetails(name) {

  let response = await API.getCompanyDetail(name.id, route)

  if (response) useMapConsigneeAddress(response, pickStock.value);

}

function handlePick(rowData) {

  emits('update-picked-items', rowData.pickQuantity)

  if (rowData.pickQuantity) {

    let isExistingRowData = props.modelValue.pickedItems.find((item) => {
      return rowData.id === item.id
    })

    if (isExistingRowData) return;

    props.modelValue.pickedItems.push(rowData);
    props.modelValue.openPicksDrawer = true;
    rowData.allocated = rowData.pickQuantity;
  }
}

function toggleSearchGroup(button) {

  if (button && pickStock.value.searchGroup) return;
  if (!button && !pickStock.value.searchGroup) return;

  pickStock.value.searchGroup = !pickStock.value.searchGroup;
}

async function getStockHeaders(selected) {

  useClearDescriptionHeaders(pickStock.value);

  const response = await API.getStockDescriptions(route, selected.id);

  if(response) {

    for(const each in response) {

      if(response[each]) {

        pickStock.value.skuGroupSelected = true;
        pickStock.value.searchPills[each].enabled = true;
        pickStock.value.searchPills[each].label = response[each];

      } else {

        pickStock.value.searchPills[each].enabled = false;

      }
    }
  }

  handlePickStockSearch();

}

async function clearHeaders() {

  useClearDescriptionHeaders(pickStock.value);

  if(useCheckSearchCriteriaExists(props.modelValue.searchCriteria)) {

    handlePickStockSearch();

  } else {

    pickStock.value.searchData = [];
  }

}

async function handlePickStockSearch() {

  const response = await API.searchStock(route, pickStock.value.searchCriteria, 'pickstock');

  pickStock.value.searchData = response;

}


function mapSelectedConsigneeContactDetail(contact) {

  useMapConsigneeContact(contact, pickStock.value);

}

function handleClearCompany() {

  pickStock.value.consignee = new ConsigneeDetail();

}

function disableGridShowButton(skuProperties) {

  for(const each in skuProperties) {
    if(skuProperties[each].value) return true
  }

  return false
}

function handleClearData() {

  pickStock.value.searchData = [];
}

</script>

<template class="cmp-pick-stock">
  <StaticWidget title="Delivery Information" height="230" background="#fcfcfc">
    <template #icon><i class="fa-regular fa-pen-field" style="color: #21426b"></i></template>
    <template #extra-content>
      <div style="margin-left: 80px; font-weight: normal">Warehouse Job Number:
        <span style="color: var(--aqua-blue-definition)">{{pickStock.jobNumber}}</span>
        <Button
            style="margin-left: 20px;"
            type="toolbar"
            text="Cancel Job">
        </Button>
      </div>
    </template>
    <template #right-side>
      <!--      <Button-->
      <!--          type="plain"-->
      <!--          icon="<i class='fa-light fa-floppy-disk'></i>"-->
      <!--          text="Save Job Details">-->
      <!--      </Button>-->
      <Button
          type="warning"
          @click="props.modelValue.openDocsDrawer = true;"
          icon="<i class='fa-solid fa-folder-plus'></i>"
          text="Add/View Docs">
      </Button>
      <Button
          type="plain"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          text="Save & Submit">
      </Button>
    </template>
    <div class="cmp-pick-stock_delivery-section-wrapper">
      <div class="cmp-pick-stock_delivery-section-layout">
        <div class="global-css_vertical-form-layout">
          <div class="global-flex-layout">
            <MultiSearchDropDown
                :key="pickStock.metaData.consignees"
                :allowCustomValue="true"
                :useServerSearch="true"
                :maxLength="50"
                searchType="consignee"
                @select="getCompanyDetails($event, 'consignee')"
                @clear="handleClearCompany('consignee')"
                label="Consignee"
                :dataSource="pickStock.metaData.consignees"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
            <StaticTooltip :icon="false">
              <template #content>
                <div style="width: 300px;">
                  Select a Consignee by typing a Company name or postcode. You can type a custom name in the same box
                  and save as a new record, or use as a one off Consignee for the booking.
                </div>
              </template>
              <i class="fa fa-info-circle" style="font-size: 14px; color: var(--info-icon-color)"></i>
            </StaticTooltip>
          </div>
          <TextInput
              v-model="pickStock.consignee.consigneeAddressLine1"
              :maxLength="50"
              label="Address Line 1">
          </TextInput>
          <TextInput
              v-model="pickStock.consignee.consigneeAddressLine2"
              :maxLength="50"
              label="Address Line 2">
          </TextInput>
          <TextInput
              v-model="pickStock.consignee.city"
              :maxLength="50"
              label="City">
          </TextInput>
          <div class="global-flex-layout">
            <TextInput
                v-model="pickStock.consignee.consigneePostcode"
                :maxLength="10"
                :width="175"
                label="Post Code / ZIP">
            </TextInput>
            <TextInput
                v-model="pickStock.consignee.consigneeCounty"
                :maxLength="50"
                :width="155"
                label="County / State">
            </TextInput>
          </div>
          <MultiSearchDropDown
              :key="pickStock.metaData.countries"
              v-model="pickStock.consignee.consigneeCountry"
              :dataSource="pickStock.metaData.countries"
              :allowCustomValue="false"
              label="Country"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
        </div>
        <div class="global-css_vertical-form-layout">
          <MultiSearchDropDown
              :key="pickStock.consignee.consigneeContacts"
              :width="400"
              :labelWidth="85"
              :maxLength="50"
              placeholder="Select..."
              :allowCustomValue="true"
              @select="mapSelectedConsigneeContactDetail"
              @clear="useClearConsigneeContact(pickStock)"
              :disableFilter="true"
              label="Contacts"
              v-model="pickStock.consignee.consigneeContactFullName"
              :data-source="pickStock.consignee.consigneeContacts"
              :selectPrimitiveValue="true">
          </MultiSearchDropDown>
          <div class="global-flex-layout">
            <TextInput
                v-model="pickStock.consignee.consigneeContactTitle"
                :maxLength="10"
                :labelWidth="85"
                :width="40"
                placeholder="Title"
                label="Name">
            </TextInput>
            <TextInput
                v-model="pickStock.consignee.consigneeContactFirstName"
                :maxLength="20"
                :width="150"
                placeholder="First Name">
            </TextInput>
            <TextInput
                v-model="pickStock.consignee.consigneeContactLastName"
                :maxLength="25"
                :width="190"
                placeholder="Last Name">
            </TextInput>
          </div>
          <TextInput
              v-model="pickStock.consignee.consigneeContactPhone"
              :width="400"
              :labelWidth="85"
              :maxLength="25"
              label="Telephone">
          </TextInput>
          <TextInput
              v-model="pickStock.consignee.consigneeContactEmail"
              :width="400"
              :labelWidth="85"
              :maxLength="50"
              :validationText="['Required', 'Invalid Email']"
              label="Email">
          </TextInput>
          <div class="cmp-pick-stock_address-save-button global-flex-layout">
            <Button
                type="plain"
                icon="<i class='fa-light fa-floppy-disk'></i>"
                text="Save Address & Contact">
            </Button>
            <!--          <Button-->
            <!--              text="Update Address & Contact"-->
            <!--              type="success">-->
            <!--          </Button>-->
            <!--          <div>-->
            <!--            <StaticTooltip :icon="false">-->
            <!--              <template #content>-->
            <!--                <div style="width: 300px;">-->
            <!--                  <div>Once you have entered Consignee information, you can select an existing contact,-->
            <!--                    edit and save the changes.-->
            <!--                  </div>-->
            <!--                  <div>If you wish to add a new contact, fill out a first & last name as a minimum. Any Consignee changes are also saved.</div>-->
            <!--                </div>-->
            <!--              </template>-->
            <!--              <i class="fa-solid fa-info-circle" style="font-size: 14px; margin-right: 5px; color: var(&#45;&#45;info-icon-color)"></i>-->
            <!--            </StaticTooltip>-->
            <!--          </div>-->
          </div>
        </div>
        <div class="cmp-pick-stock_job-details-area global-css_vertical-form-layout">
          <TextInput
              v-model="pickStock.customerRef"
              :width="365"
              :maxLength="50"
              :labelWidth="120"
              label="Customer Ref">
          </TextInput>
          <div class="global-flex-layout">
            <DateInput
                v-model="pickStock.releaseDate"
                :labelWidth="118"
                label="Release Date">
              <el-date-picker v-model="pickStock.releaseDate"></el-date-picker>
            </DateInput>
            <MultiSearchDropDown
                v-model="pickStock.releaseTime"
                style="margin-left: 35px;"
                :width="121"
                :labelWidth="75"
                :maxLength="50"
                placeholder="Select..."
                :allowCustomValue="false"
                :disableFilter="true"
                label="Release Time"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
          </div>
          <DateInput
              v-model="pickStock.deliveryDate"
              :labelWidth="118"
              label="Delivery Date">
            <el-date-picker></el-date-picker>
          </DateInput>
          <TextInput
              v-model="pickStock.action"
              :width="365"
              :maxLength="50"
              :labelWidth="120"
              label="Action">
          </TextInput>
          <DropDownTextArea
              v-model="pickStock.deliveryInstructions"
              :width="365"
              :labelWidth="120"
              label="Delivery Instructions">
          </DropDownTextArea>
        </div>
      </div>
    </div>
  </StaticWidget>
  <StaticWidget title="Search Stock" height="420">
    <template #extra-content>
      <div style=" margin-left: 80px; font-weight: normal">SKU Group</div>
      <div style="font-weight: normal" class="global-flex-layout">
        <MultiSearchDropDown
            :key="pickStock.metaData.skuGroups"
            :width="320"
            :labelWidth="85"
            :maxLength="50"
            placeholder="Select..."
            @select="getStockHeaders"
            @clear="clearHeaders"
            :allowCustomValue="false"
            :disableFilter="true"
            v-model="pickStock.searchCriteria.skuGroup"
            :dataSource="pickStock.metaData.skuGroups"
            :selectPrimitiveValue="true">
        </MultiSearchDropDown>
        <StaticTooltip :icon="false">
          <template #content>
            <div style="width: 340px;">
              <strong>Search</strong> by completing any field and press any search icon
              <span><i class="fa-duotone fa-square-arrow-right" style="color: #114362"></i></span> or press enter within
              the field.
              Any fields with data are considered when searching records, some criteria may return no results. Selecting
              a
              SKU Group will bring new search criteria where available. A search group toggle will appear to view other
              fields.
              Press Refresh to return the latest records for the current search, or reset the data to initial records.
            </div>
          </template>
          <i class="fa-solid fa-circle-info icon-info"></i>
        </StaticTooltip>
        <div v-if="pickStock.skuGroupSelected" class="cmp-stock_search-group-selection">
          <span>Search Group</span>
          <Button
              @click="toggleSearchGroup(true)"
              type="toolbar"
              text="1"
              :class="{'cmp-stock_search-group-selection-icon-active' : pickStock.searchGroup}"
              style="width: 30px;">
          </Button>
          <Button
              @click="toggleSearchGroup(false)"
              type="toolbar"
              text="2"
              :class="{'cmp-stock_search-group-selection-icon-active' : !pickStock.searchGroup}"
              style="width: 30px;">
          </Button>
        </div>
      </div>
    </template>
    <template #right-side>
<!--      <Button-->
<!--          @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"-->
<!--          icon="<i class='fa-duotone fa-grid-2'></i>"-->
<!--          type="info"-->
<!--          :badgeData="pickStock.pickedItems.length"-->
<!--          text="View Picks">-->
<!--      </Button>-->
    </template>
    <SearchStockPillGroup
        url="pickstock"
        @clear="handleClearData"
        v-model="pickStock">
    </SearchStockPillGroup>
    <div style="margin-top: 50px;">
      <DataGrid
          :key="pickStock.searchData.length"
          :allowGridMenu="false"
          height="300"
          :page-size="200"
          :selectable="false"
          :row-selection="false"
          v-model:currentPage="pickStock.searchResultCurrentPage"
          v-model:currentPaginationRange="pickStock.searchResultPaginationRange"
          :dataSource="pickStock.searchData"
          :columns="pickStock.searchGridColumns">
<!--        <template v-slot:available="row">-->
<!--          <div style="display: grid; grid-template-columns: 40px 100px;">-->
<!--            {{row.rowData.available}}<span v-if="row.rowData.isLowStock" style="font-size: 11px; color: #b04545">-->
<!--            <i class="fa-solid fa-triangle-exclamation" style="padding-top: 6px"></i> Low Stock!</span>-->
<!--          </div>-->
<!--        </template>-->
        <template v-slot:pick="row">
          <Button
              :disabled="row.rowData.isZeroQuantity"
              @click="handlePick(row.rowData)"
              icon="<i class='fa-duotone fa-circle-plus'></i>"
              type="success"
              text="Pick">
          </Button>
        </template>
        <template #menu-slot-items v-if="pickStock.searchData.length > 0">
          <Button
              @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"
              icon="<i class='fa-duotone fa-grid-2'></i>"
              type="toolbar"
              :badgeData="pickStock.pickedItems.length"
              text="View Picks">
          </Button>
            <Button
                @click="handlePick(row.rowData)"
                type="toolbar"
                text="Pick All">
            </Button>
<!--          <div>Stock Over Pick Available-->
<!--            <i class="fa-solid fa-circle-check" style="color: #3f9968;"></i>-->
<!--          </div>-->
        </template>
        <template v-slot:pickQuantity="row">
          <TextInput
              :disable="row.rowData.isZeroQuantity"
              v-model="row.rowData.pickQuantity"
              :width="60"
              :maxLength="8">
          </TextInput>
        </template>
        <template v-slot:comments="row">
          <DropDownTextArea
              v-model="row.rowData.comments"
              :width="310">
          </DropDownTextArea>
        </template>
        <template v-slot:skuProperties="row">
          <GridCellDetail :clickToView="true">
            <template #details>
              <MoreInfoPopOutPanel
                  v-model="row.grid">
                <template #header>Stock Item Properties</template>
                <template #header-extra>SKU Number:
                  <span style="color: var(--aqua-blue-definition)"> {{ row.grid.skuNumber }}</span></template>
                <template #content>
                  <StockDescriptions v-model="row.grid.skuProperties"></StockDescriptions>
                </template>
              </MoreInfoPopOutPanel>
            </template>
            <template #cell-data>
              <Button
                  v-if="disableGridShowButton(row.grid.skuProperties)"
                  type="toolbar"
                  text="View">
              </Button>
            </template>
          </GridCellDetail>
        </template>
      </DataGrid>
    </div>
  </StaticWidget>
</template>

<style lang="scss" scoped>

.cmp-pick-stock {

  &_delivery-section-layout {
    padding: 20px;
    display: grid;
    gap: 70px;
    grid-template-columns: 590px 490px 500px;
  }

  &_delivery-section-wrapper {
    background-color: #ffffff;
    border-radius: var(--rounded-corners);
    border: 1px solid var(--border-color);
    border-left: 6px solid var(--left-border-highlight);
  }

  &_address-save-button {
    padding: 5px 0 0 88px;
  }

  &_job-details-area {
    margin-top: -10px;
    padding: 10px;
  }

}

</style>
